import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { ar, de, enUS, es, fr, it, zhCN, zhTW } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  PartnerEmptyState,
  PartnerLoading,
  PartnerNote,
  PartnerPageHeader,
  PartnerPageShell,
  partnerOxbloodBtnSx,
  partnerOutlineBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';
import PartnerClientInvite from './PartnerClientInvite';
import PartnerPlanWall from '../components/PartnerPlanWall';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import { canUsePartnerClientInvite } from '../utils/partnerPlan';

const DATE_LOCALES = { en: enUS, de, es, fr, it, ar, 'zh-CN': zhCN, 'zh-TW': zhTW };

const clientFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: DOCUMENT.surface,
    fontFamily: DOCUMENT.sans,
    '& fieldset': { borderColor: DOCUMENT.ruleHard },
    '&:hover fieldset': { borderColor: DOCUMENT.ink },
    '&.Mui-focused fieldset': { borderColor: DOCUMENT.ink, borderWidth: 1 },
  },
  '& .MuiInputLabel-root': { fontFamily: DOCUMENT.sans, color: DOCUMENT.ink3 },
  '& .MuiInputLabel-root.Mui-focused': { color: DOCUMENT.ink },
};

const tripWhen = (value, locale) => {
  if (!value) return '';
  const parsed = parseISO(String(value).slice(0, 10));
  if (Number.isNaN(parsed.getTime())) return '';
  return format(parsed, 'MMM d', { locale });
};

const contactedWhen = (value, locale) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatDistanceToNow(parsed, { addSuffix: true, locale });
};

const ClientRosterRow = ({ client, onOpen }) => {
  const { t, i18n } = useTranslation();
  const locale = DATE_LOCALES[i18n.language] || enUS;
  const name = client.display_name || client.username || client.email;
  const money = `$${Number(client.lifetime_booking_value || 0).toLocaleString()}`;
  const nextDate = tripWhen(client.next_trip_start, locale);
  const nextTrip = client.next_trip_title
    ? (nextDate ? `${client.next_trip_title} · ${nextDate}` : client.next_trip_title)
    : t('partnerCrm.notYet');
  const contacted = contactedWhen(client.last_contacted_at, locale) || t('partnerCrm.notYet');
  const joined = client.status === 'joined';
  const lineSx = {
    fontSize: 13,
    lineHeight: 1.35,
    color: DOCUMENT.ink2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={t('partnerCrm.viewClientName', { name })}
      data-testid={`client-row-${client.id}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(140px, 1.1fr) minmax(150px, 0.9fr) minmax(160px, 1.3fr) max-content' },
        columnGap: 2,
        rowGap: 0.25,
        alignItems: 'center',
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${DOCUMENT.ruleHard}`,
        bgcolor: DOCUMENT.surface,
        borderRadius: 0,
        px: { xs: 1.5, md: 2 },
        py: 1.25,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        '&:hover': { borderColor: DOCUMENT.oxblood },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, fontWeight: 400, color: DOCUMENT.ink, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </Typography>
        <Typography sx={{ fontSize: 12, lineHeight: 1.3, color: DOCUMENT.ink3, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {client.email}
        </Typography>
      </Box>
      <Typography sx={{ ...lineSx, color: DOCUMENT.ink }}>
        {`${t('partnerCrm.tripCount', { count: Number(client.trip_count || 0) })} · ${t('partnerCrm.valueLine', { value: money })}`}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={lineSx}>
          {t('partnerCrm.nextShort', { trip: nextTrip })}
        </Typography>
        <Typography sx={{ ...lineSx, mt: 0.25 }}>
          {t('partnerCrm.lastContacted', { when: contacted })}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 13, lineHeight: 1.35, color: DOCUMENT.ink2, whiteSpace: 'nowrap' }}>
        {`${joined ? t('partnerCrm.statusJoined') : t('partnerCrm.statusInvited')} · `}
        <Box component="span" sx={{ color: DOCUMENT.oxblood }}>
          {t('partnerCrm.viewClient')}
        </Box>
      </Typography>
    </Box>
  );
};

const PartnerClients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { entitlements, loading: entitlementsLoading } = usePartnerEntitlements();
  const canAssociateClients = canUsePartnerClientInvite(entitlements);
  const [planWallOpen, setPlanWallOpen] = useState(false);
  const [tab, setTab] = useState('crm');
  const [clients, setClients] = useState([]);
  const [links, setLinks] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [booted, setBooted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [automation, setAutomation] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', email: '', phone: '', notes: '' });
  const bootedRef = React.useRef(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('partnerToken')}` });

  const load = useCallback(async () => {
    if (!localStorage.getItem('partnerToken')) {
      navigate('/partner/login');
      return;
    }
    if (!bootedRef.current) setLoading(true);
    try {
      const [clientsRes, linksRes, autoRes] = await Promise.all([
        api.get('/partners/crm/clients', { headers: headers(), params: { q: q || undefined } }),
        api.get('/partners/intake-links', { headers: headers() }),
        api.get('/partners/automations', { headers: headers() }),
      ]);
      setClients(clientsRes.data.clients || []);
      setLinks(linksRes.data.links || []);
      setAutomation(autoRes.data.settings || null);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || t('partnerCrm.loadFailed'));
    } finally {
      setLoading(false);
      bootedRef.current = true;
      setBooted(true);
    }
  }, [navigate, q, t]);

  useEffect(() => {
    load();
  }, [load]);

  const createIntakeLink = async () => {
    try {
      const res = await api.post('/partners/intake-links', { label: t('partnerIntake.defaultLabel') }, { headers: headers() });
      setLinks((prev) => [res.data.link, ...prev]);
      setSuccess(t('partnerCrm.intakeCreated'));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerCrm.intakeCreateFailed'));
    }
  };

  const closeImport = () => {
    setImportOpen(false);
    setImportFile(null);
    setImportSummary(null);
    setImporting(false);
  };

  const downloadImportTemplate = () => {
    const csv = 'email,name,phone,notes\nada@example.com,Ada Lovelace,555-0100,Prefers trains\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'clients-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const importClients = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      setError('');
      const csv = await importFile.text();
      const res = await api.post('/partners/crm/clients/import', { csv }, { headers: headers() });
      setImportSummary(res.data);
      setSuccess(t('partnerCrm.importDone', { count: res.data.imported || 0 }));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerCrm.importFailed'));
    } finally {
      setImporting(false);
    }
  };

  const openNewClient = () => {
    if (!canAssociateClients) {
      setPlanWallOpen(true);
      return;
    }
    setDraft({ name: '', email: '', phone: '', notes: '' });
    setCreateOpen(true);
  };

  const saveNewClient = async () => {
    const email = draft.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('partnerCrm.newClientEmailInvalid'));
      return;
    }
    try {
      setCreating(true);
      setError('');
      await api.post('/partners/crm/clients', {
        email,
        displayName: draft.name.trim(),
        phone: draft.phone.trim(),
        notes: draft.notes.trim(),
      }, { headers: headers() });
      setCreateOpen(false);
      setSuccess(t('partnerCrm.newClientDone'));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerCrm.newClientFailed'));
    } finally {
      setCreating(false);
    }
  };

  const toggleAutomation = async (key) => {
    const next = { ...automation, [key]: !automation[key] };
    setAutomation(next);
    try {
      await api.put('/partners/automations', { settings: next }, { headers: headers() });
    } catch (err) {
      setError(err.response?.data?.error || t('partnerCrm.automationSaveFailed'));
      load();
    }
  };

  return (
    <PartnerPageShell>
      <PartnerPageHeader
        title={t('partnerCrm.title')}
        subtitle={t('partnerCrm.subtitle')}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button sx={partnerOxbloodBtnSx} data-testid="new-client" onClick={openNewClient}>
              {t('partnerCrm.newClient')}
            </Button>
            <Button
              sx={partnerOutlineBtnSx}
              data-testid="import-clients"
              onClick={() => (canAssociateClients ? setImportOpen(true) : setPlanWallOpen(true))}
            >
              {t('partnerCrm.import')}
            </Button>
            <Button sx={partnerOutlineBtnSx} onClick={() => setTab(tab === 'intake' ? 'crm' : 'intake')}>{t('partnerCrm.tabIntake')}</Button>
            <Button sx={partnerOutlineBtnSx} onClick={() => setTab(tab === 'invite' ? 'crm' : 'invite')}>{t('partnerCrm.tabInvite')}</Button>
            <Button sx={partnerOutlineBtnSx} onClick={() => setTab(tab === 'automations' ? 'crm' : 'automations')}>{t('partnerCrm.tabAutomations')}</Button>
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {tab === 'invite' ? (
        <PartnerClientInvite embedded />
      ) : (!booted || loading) ? (
        <PartnerLoading />
      ) : tab === 'intake' ? (
        <Box>
          <PartnerNote>{t('partnerCrm.intakeHelp')}</PartnerNote>
          <Button onClick={createIntakeLink} sx={{ ...partnerOxbloodBtnSx, mb: 2 }}>{t('partnerCrm.createIntake')}</Button>
          {links.length === 0 ? (
            <PartnerEmptyState title={t('partnerCrm.noIntake')} />
          ) : (
            <Stack spacing={1.5}>
              {links.map((link) => (
                <Box key={link.id} sx={{ borderBottom: `1px solid ${DOCUMENT.rule}`, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 600 }}>{link.label}</Typography>
                  <Typography variant="body2" sx={{ color: DOCUMENT.ink3, wordBreak: 'break-all' }}>{link.url}</Typography>
                  <Chip size="small" label={link.active ? t('partnerCrm.active') : t('partnerCrm.inactive')} sx={{ mt: 1, borderRadius: 0 }} />
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      ) : tab === 'automations' && automation ? (
        <Stack spacing={1.5}>
          <PartnerNote>{t('partnerCrm.automationsHelp')}</PartnerNote>
          {Object.keys(automation).map((key) => (
            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${DOCUMENT.rule}`, py: 1 }}>
              <Typography>{t(`partnerCrm.auto.${key}`, key)}</Typography>
              <Button size="small" sx={partnerOutlineBtnSx} onClick={() => toggleAutomation(key)}>
                {automation[key] ? t('partnerCrm.on') : t('partnerCrm.off')}
              </Button>
            </Box>
          ))}
        </Stack>
      ) : (
        <Box>
          {!entitlementsLoading && !canAssociateClients ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                mt: '19px',
                mb: 1.5,
                px: 2,
                py: 1,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                bgcolor: DOCUMENT.ivory,
              }}
            >
              <Typography sx={{ fontSize: 14, color: DOCUMENT.ink, lineHeight: 1.4 }}>
                {t('partnerCrm.enterpriseBanner')}
              </Typography>
              <Button sx={partnerOxbloodBtnSx} onClick={() => setPlanWallOpen(true)}>
                {t('partnerClientInvite.viewPlans')}
              </Button>
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: !entitlementsLoading && !canAssociateClients ? 0 : '19px', mb: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder={t('partnerCrm.search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              inputProps={{ 'aria-label': t('partnerCrm.search') }}
              sx={{
                flex: '1 1 240px',
                maxWidth: 360,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  bgcolor: DOCUMENT.surface,
                  minHeight: 40,
                  fontFamily: DOCUMENT.sans,
                  '& fieldset': { borderColor: DOCUMENT.ruleHard },
                  '&:hover fieldset': { borderColor: DOCUMENT.ink },
                  '&.Mui-focused fieldset': { borderColor: DOCUMENT.ink },
                },
                '& .MuiOutlinedInput-input': {
                  py: 1.15,
                  px: 1.75,
                  fontSize: 14,
                },
              }}
            />
          </Box>
          {clients.length === 0 ? (
            <PartnerEmptyState title={t('partnerCrm.noClients')} body={t('partnerCrm.noClientsBody')} />
          ) : (
            <Stack spacing={1}>
              {clients.map((client) => (
                <ClientRosterRow
                  key={client.id}
                  client={client}
                  onOpen={() => navigate(`/partner/clients/${client.id}`)}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}

      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 0,
            boxShadow: 'none',
            border: `1px solid ${DOCUMENT.ruleHard}`,
            bgcolor: DOCUMENT.surface,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, color: DOCUMENT.ink }}>
          {t('partnerCrm.newClientTitle')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <TextField
              label={t('partnerCrm.newClientName')}
              value={draft.name}
              onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
              sx={clientFieldSx}
            />
            <TextField
              required
              label={t('partnerCrm.newClientEmail')}
              value={draft.email}
              onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))}
              sx={clientFieldSx}
            />
            <TextField
              label={t('partnerCrm.newClientPhone')}
              value={draft.phone}
              onChange={(event) => setDraft((prev) => ({ ...prev, phone: event.target.value }))}
              sx={clientFieldSx}
            />
            <TextField
              label={t('partnerCrm.newClientNotes')}
              value={draft.notes}
              onChange={(event) => setDraft((prev) => ({ ...prev, notes: event.target.value }))}
              multiline
              minRows={2}
              sx={clientFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating} sx={partnerOutlineBtnSx}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={saveNewClient}
            disabled={creating || !draft.email.trim()}
            sx={partnerOxbloodBtnSx}
            data-testid="new-client-submit"
          >
            {creating ? t('partnerCrm.newClientSaving') : t('partnerCrm.newClientSave')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={importOpen}
        onClose={closeImport}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 0,
            boxShadow: 'none',
            border: `1px solid ${DOCUMENT.ruleHard}`,
            bgcolor: DOCUMENT.surface,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, color: DOCUMENT.ink }}>
          {t('partnerCrm.importTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mb: 2 }}>
            {t('partnerCrm.importHelp')}
          </Typography>
          <Button sx={{ ...partnerOutlineBtnSx, mb: 2 }} onClick={downloadImportTemplate}>
            {t('partnerCrm.importTemplate')}
          </Button>
          <Box>
            <input
              type="file"
              accept=".csv,text/csv"
              data-testid="client-import-file"
              onChange={(event) => {
                setImportFile(event.target.files?.[0] || null);
                setImportSummary(null);
              }}
            />
          </Box>
          {importSummary ? (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 14, color: DOCUMENT.ink }}>
                {t('partnerCrm.importSummary', {
                  imported: importSummary.imported || 0,
                  skipped: (importSummary.skipped || []).length,
                })}
              </Typography>
              {(importSummary.skipped || []).slice(0, 5).map((row) => (
                <Typography key={`${row.row}-${row.email}`} sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5 }}>
                  {`${row.email || row.row}: ${row.error}`}
                </Typography>
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeImport} sx={partnerOutlineBtnSx}>{t('common.cancel')}</Button>
          <Button
            onClick={importClients}
            disabled={!importFile || importing}
            sx={partnerOxbloodBtnSx}
            data-testid="client-import-submit"
          >
            {importing ? t('partnerCrm.importing') : t('partnerCrm.import')}
          </Button>
        </DialogActions>
      </Dialog>
      <PartnerPlanWall
        open={planWallOpen}
        onClose={() => setPlanWallOpen(false)}
        reason="clients"
        entitlements={entitlements}
      />
    </PartnerPageShell>
  );
};

export default PartnerClients;
